import { canAccessEexPath } from "./eex-access";
import type { AuthUser } from "../stores/auth";

export type NavigationSectionId =
  | "trading"
  | "forecasting"
  | "eex"
  | "brm"
  | "admin";

export interface NavigationItem {
  to: string;
  label: string;
  roles?: AuthUser["role"][];
  requiresEexDatasource?: boolean;
}

export interface NavigationSection {
  id: NavigationSectionId;
  label: string;
  mobileLabel: string;
  landingTo: string;
  placement: "chapter" | "utility";
  items: NavigationItem[];
}

type NavigationSectionDefinition = NavigationSection & {
  isVisible(user: AuthUser): boolean;
  isActive(pathname: string): boolean;
};

const TRADING_ITEMS: NavigationItem[] = [
  { to: "/grid", label: "Position & P&L" },
  { to: "/balancing", label: "Balancing" },
  { to: "/trades", label: "Trades" },
  { to: "/reports", label: "Reports" },
  { to: "/generator", label: "Generator" },
  { to: "/ingest", label: "Ingest" },
];

const FORECASTING_ITEMS: NavigationItem[] = [
  { to: "/forecasting", label: "DAM Prices" },
  { to: "/forecasting/contracts", label: "Contracts" },
  { to: "/forecasting/profile-builder", label: "Profile builder" },
  { to: "/forecasting/month-view", label: "Month view" },
  { to: "/forecasting/consumption", label: "Consumption" },
];

const EEX_ITEMS: NavigationItem[] = [
  { to: "/eex/trades", label: "Trades" },
  { to: "/eex/quotes", label: "Quotes" },
  { to: "/eex/fx", label: "FX" },
  { to: "/eex/matching", label: "Matching" },
  { to: "/eex/positions-tree", label: "Positions" },
  { to: "/eex/stats", label: "Stats" },
  { to: "/eex/statements", label: "Statements" },
  { to: "/eex/margin", label: "Margin" },
  { to: "/eex/margin-lab", label: "Margin Lab" },
  { to: "/eex/pnl", label: "P&L" },
  { to: "/eex/recognition-schedules", label: "Recognition schedules" },
  { to: "/eex/delivery", label: "Delivery" },
  {
    to: "/eex/market-data",
    label: "Market Data",
    roles: ["company_admin", "trader", "risk", "viewer"],
    requiresEexDatasource: true,
  },
  { to: "/eex/research", label: "Research" },
  { to: "/eex/settings", label: "Settings" },
];

const ADMIN_ITEMS: NavigationItem[] = [
  { to: "/admin/inbox", label: "Inbox", roles: ["global_admin"] },
  { to: "/admin/clients", label: "Clients", roles: ["global_admin"] },
  { to: "/admin/onboarding", label: "Onboarding", roles: ["global_admin"] },
  { to: "/admin/guide", label: "Guide", roles: ["global_admin"] },
  { to: "/admin/transactions", label: "Transactions", roles: ["global_admin"] },
  {
    to: "/admin/users",
    label: "Users",
    roles: ["global_admin", "company_admin"],
  },
  {
    to: "/admin/global-admins",
    label: "Global admins",
    roles: ["global_admin"],
  },
  {
    to: "/admin/billing",
    label: "Billing",
    roles: ["global_admin", "company_admin"],
  },
  {
    to: "/admin/entsoe",
    label: "ENTSO-E",
    roles: ["global_admin", "company_admin"],
  },
  {
    to: "/admin/activity",
    label: "Activity",
    roles: ["global_admin", "company_admin"],
  },
];

const NAVIGATION_CATALOG: NavigationSectionDefinition[] = [
  {
    id: "trading",
    label: "DA/IDM/BM Trading",
    mobileLabel: "Trading",
    landingTo: "/grid",
    placement: "chapter",
    items: TRADING_ITEMS,
    isVisible: (user) => Boolean(user.tradingEnabled),
    isActive: (pathname) =>
      !pathname.startsWith("/admin") &&
      !pathname.startsWith("/forecasting") &&
      !pathname.startsWith("/settings") &&
      !pathname.startsWith("/eex"),
  },
  {
    id: "forecasting",
    label: "Forecasting",
    mobileLabel: "Forecasting",
    landingTo: "/forecasting",
    placement: "chapter",
    items: FORECASTING_ITEMS,
    isVisible: (user) => Boolean(user.forecastingEnabled),
    isActive: (pathname) => pathname.startsWith("/forecasting"),
  },
  {
    id: "eex",
    label: "EEX",
    mobileLabel: "EEX",
    landingTo: "/eex/trades",
    placement: "chapter",
    items: EEX_ITEMS,
    isVisible: (user) => Boolean(user.eexEnabled),
    isActive: (pathname) => pathname.startsWith("/eex"),
  },
  {
    id: "brm",
    label: "BRM",
    mobileLabel: "BRM",
    landingTo: "/settings/brm",
    placement: "utility",
    items: [{ to: "/settings/brm", label: "BRM settings" }],
    // Keep the existing shell rule: global admins, risk and viewer users do not
    // receive the BRM settings link even if a module flag is present.
    isVisible: (user) =>
      Boolean(user.brmEnabled) &&
      (user.role === "company_admin" || user.role === "trader"),
    isActive: (pathname) => pathname.startsWith("/settings/brm"),
  },
  {
    id: "admin",
    label: "Admin",
    mobileLabel: "Admin",
    landingTo: "/admin",
    placement: "utility",
    items: ADMIN_ITEMS,
    isVisible: (user) =>
      user.role === "global_admin" || user.role === "company_admin",
    isActive: (pathname) => pathname.startsWith("/admin"),
  },
];

export function navigationFor(user: AuthUser): NavigationSection[] {
  return NAVIGATION_CATALOG.filter(section => section.isVisible(user) && (user.role !== "finance" || section.id === "eex")).map(section => {
    const items = section.items.filter(item =>
      (section.id !== "eex" || canAccessEexPath(user, item.to)) &&
      (!item.roles || item.roles.includes(user.role)) &&
      (!item.requiresEexDatasource || Boolean(user.eexDatasourceEnabled)));
    return { id: section.id, label: section.label, mobileLabel: section.mobileLabel,
      landingTo: section.id === "eex" ? items[0]?.to ?? section.landingTo : section.landingTo,
      placement: section.placement, items };
  }).filter(section => section.items.length > 0);
}

const MOBILE_PHASE_1_PATHS = new Set([
  "/grid",
  "/balancing",
  "/trades",
  "/eex/trades",
  "/eex/positions-tree",
  "/eex/stats",
  "/eex/statements",
  "/eex/margin",
  "/eex/pnl",
  "/eex/recognition-schedules",
  "/eex/delivery",
  "/eex/market-data",
]);

export function mobileNavigationFor(user: AuthUser): NavigationSection[] {
  return navigationFor(user)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => MOBILE_PHASE_1_PATHS.has(item.to)),
    }))
    .filter((section) => section.items.length > 0);
}

export function isNavigationSectionActive(
  sectionId: NavigationSectionId,
  pathname: string
): boolean {
  return (
    NAVIGATION_CATALOG.find((section) => section.id === sectionId)?.isActive(
      pathname
    ) ?? false
  );
}

export function isMobileNavigationSectionActive(
  sectionId: NavigationSectionId,
  pathname: string
): boolean {
  return pathname !== "/" && isNavigationSectionActive(sectionId, pathname);
}

const STATIC_PAGE_TITLES: Record<string, string> = {
  "/": "Dashboard",
  "/login": "Sign in",
  "/change-password": "Change password",
  "/admin": "Admin",
  "/admin/": "Admin",
  "/eex/": "EEX",
};

export function navigationPageTitle(pathname: string): string {
  const normalizedPathname =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  const staticTitle =
    STATIC_PAGE_TITLES[pathname] ?? STATIC_PAGE_TITLES[normalizedPathname];
  if (staticTitle) return staticTitle;

  for (const section of NAVIGATION_CATALOG) {
    const item = section.items.find((candidate) => candidate.to === normalizedPathname);
    if (item) return item.label.replace(" ▸", "");
  }

  if (normalizedPathname.startsWith("/admin")) return "Admin";
  if (normalizedPathname.startsWith("/forecasting")) return "Forecasting";
  if (normalizedPathname.startsWith("/settings/brm")) return "BRM";
  if (normalizedPathname.startsWith("/eex")) return "EEX";
  return "CASCENA";
}
